import { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
// @mui
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
//
import { IftaFiling } from 'src/models';

// ----------------------------------------------------------------------

type VehicleSummary = {
  truckNumber: string;
  totalMiles: number;
  totalGallons: number;
  states: Set<string>;
};

function summarize(filing: IftaFiling): VehicleSummary[] {
  const byTruck = new Map<string, VehicleSummary>();
  const get = (truckNumber: string) => {
    let summary = byTruck.get(truckNumber);
    if (!summary) {
      summary = { truckNumber, totalMiles: 0, totalGallons: 0, states: new Set() };
      byTruck.set(truckNumber, summary);
    }
    return summary;
  };
  filing.mileLines.forEach((line) => {
    const summary = get(line.truckNumber);
    summary.totalMiles += line.totalMiles;
    summary.states.add(line.state);
  });
  filing.fuelLines.forEach((line) => {
    const summary = get(line.truckNumber);
    summary.totalGallons += line.gallons;
    summary.states.add(line.state);
  });
  return Array.from(byTruck.values()).sort((a, b) => a.truckNumber.localeCompare(b.truckNumber));
}

// Same flex-column virtualized layout as the Miles/Fuel editors.
const cellSx = (flex: string, extra?: object) => ({
  flex,
  display: 'flex',
  alignItems: 'center',
  ...extra,
});

const TRUCK_FLEX = '0 0 190px';
const NUM_FLEX = '1 1 0';

export default function VehiclesTab({ filing }: { filing: IftaFiling }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const vehicles = useMemo(() => summarize(filing), [filing]);

  const virtualizer = useVirtualizer({
    count: vehicles.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 39,
    overscan: 12,
  });
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <Stack spacing={1}>
      <TableContainer
        ref={scrollRef}
        data-testid="vehicles-table"
        sx={{ maxHeight: 420, width: 'auto', mx: { xs: -2, md: -4 } }}
      >
        <Table size="small" sx={{ display: 'block', minWidth: 640 }}>
          <TableHead sx={{ display: 'block', position: 'sticky', top: 0, zIndex: 2 }}>
            <TableRow sx={{ display: 'flex' }}>
              <TableCell sx={cellSx(TRUCK_FLEX, { pl: { xs: 2, md: 4 } })}>Truck</TableCell>
              <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>Total Miles</TableCell>
              <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>
                Gallons Purchased
              </TableCell>
              <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>Avg MPG</TableCell>
              <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end', pr: { xs: 2, md: 4 } })}>
                Jurisdictions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{ display: 'block', position: 'relative', height: virtualizer.getTotalSize() }}
          >
            {virtualItems.map((virtualRow) => {
              const vehicle = vehicles[virtualRow.index];
              return (
                <TableRow
                  key={vehicle.truckNumber}
                  sx={{
                    display: 'flex',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: 39,
                    bgcolor: virtualRow.index % 2 === 1 ? '#FAFBFA' : '#FFFFFF',
                  }}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <TableCell sx={cellSx(TRUCK_FLEX, { pl: { xs: 2, md: 4 } })}>
                    {vehicle.truckNumber}
                  </TableCell>
                  <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>
                    {vehicle.totalMiles.toLocaleString()}
                  </TableCell>
                  <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>
                    {vehicle.totalGallons.toLocaleString()}
                  </TableCell>
                  <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end' })}>
                    {vehicle.totalGallons > 0
                      ? (vehicle.totalMiles / vehicle.totalGallons).toFixed(1)
                      : '–'}
                  </TableCell>
                  <TableCell sx={cellSx(NUM_FLEX, { justifyContent: 'flex-end', pr: { xs: 2, md: 4 } })}>
                    {vehicle.states.size}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {vehicles.length} trucks total. Your filing price is based on this count.
      </Typography>
    </Stack>
  );
}
